'use client';

// cont-ui-2b: Stepper horizontal genérico.
//
// Renderiza N passos com estado (active/done/pending/error) + linha conectora.
// Steps `done` clicáveis (volta). Steps `pending` desabilitados. Step `active` é o atual.

import type { ReactNode } from 'react';

export type StepStatus = 'active' | 'done' | 'pending' | 'error';

export interface Step {
  label: string;
  status: StepStatus;
  hint?: string;
}

interface StepperProps {
  steps: Step[];
  /** Índice do passo ativo (0-based). Reservado pra navegação futura — atualmente o `status` da Step indica visualmente. */
  current?: number;
  /** Callback ao clicar num passo navegável (done). Steps pending são ignorados. */
  onStepClick?: (index: number) => void;
  className?: string;
}

const STATUS_CIRCLE: Record<StepStatus, string> = {
  active: 'bg-[var(--stepper-ativo)] text-white border-[var(--stepper-ativo)]',
  done: 'bg-[var(--stepper-completo)] text-white border-[var(--stepper-completo)]',
  pending: 'bg-white text-[var(--fg-muted)] border-[var(--stepper-pendente)]',
  error: 'bg-[var(--error-50)] text-[var(--error-700)] border-[var(--stepper-erro)]',
};

const STATUS_LABEL: Record<StepStatus, string> = {
  active: 'text-[var(--fg)] font-semibold',
  done: 'text-[var(--fg-muted)]',
  pending: 'text-[var(--fg-subtle)]',
  error: 'text-[var(--error-700)] font-semibold',
};

const STATUS_HINT: Record<StepStatus, string> = {
  active: 'text-[var(--brand-700)]',
  done: 'text-[var(--success-700)]',
  pending: 'text-[var(--fg-subtle)]',
  error: 'text-[var(--error-700)]',
};

const STATUS_ICON: Record<StepStatus, ReactNode> = {
  active: null,
  done: <i className="pi pi-check text-xs" aria-hidden="true" />,
  pending: null,
  error: <i className="pi pi-exclamation-triangle text-xs" aria-hidden="true" />,
};

export default function Stepper({ steps, onStepClick, className = '' }: StepperProps) {
  return (
    <ol
      className={`flex items-start gap-2 ${className}`}
      aria-label="Etapas do cadastro"
    >
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        const isClickable = step.status === 'done' && onStepClick !== undefined;
        const number = index + 1;

        return (
          <li key={step.label} className="flex flex-1 flex-col items-stretch">
            <div className="flex items-center gap-2">
              {/* Bolinha do passo */}
              <button
                type="button"
                disabled={!isClickable}
                onClick={() => isClickable && onStepClick?.(index)}
                className={`flex size-7 items-center justify-center rounded-full border-2 text-xs font-semibold transition-colors ${STATUS_CIRCLE[step.status]} ${isClickable ? 'cursor-pointer hover:opacity-90' : 'cursor-default'}`}
                aria-current={step.status === 'active' ? 'step' : undefined}
                aria-label={`Etapa ${number}: ${step.label} (${step.status})`}
              >
                {STATUS_ICON[step.status] ?? number}
              </button>

              {/* Linha conectora — entre passos */}
              {!isLast && (
                <div
                  className={`stepper-line h-0.5 flex-1 ${
                    steps[index + 1]?.status === 'done' || step.status === 'done'
                      ? 'bg-[var(--stepper-completo)]'
                      : 'bg-[var(--stepper-pendente)]'
                  }`}
                  aria-hidden="true"
                />
              )}
            </div>

            {/* Label + hint embaixo */}
            <div className="mt-1.5 pr-2">
              <p className={`text-xs ${STATUS_LABEL[step.status]}`}>
                <span className="font-mono text-[10px] opacity-60 mr-1">{number}</span>
                {step.label}
              </p>
              {step.hint && (
                <p className={`text-[11px] ${STATUS_HINT[step.status]}`}>
                  {step.hint}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
